import React from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useTheme } from "@mui/material";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { translateNormalizedRole } from "@utils/roles";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { formatDateTime, getStatusLabel } from "./utils";

const DEFAULT_ATTACHMENT_NAME = "attachment";

type InterruptionSideDrawerProps = {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  status?: string | null;
  fakeOverrideStatus?: string | null;
  externalNotesInterruption?: string | null;
  externalAttachmentsUrlInterruption?: string | null;
  externalAttachmentsNameInterruption?: string | null;
  rejectionExternalNotes?: string | null;
  rejectionExternalAttachmentsUrl?: string | null;
  rejectionExternalAttachmentsName?: string | null;
  rejectionInternalNotes?: string | null;
  rejectionInternalAttachmentsUrl?: string | null;
  rejectionInternalAttachmentsName?: string | null;
  internalNotesInterruption?: string | null;
  internalAttachmentsUrlInterruption?: string | null;
  internalAttachmentsNameInterruption?: string | null;
  externalNotes?: string | null;
  externalAttachmentsUrl?: string | null;
  externalAttachmentsName?: string | null;
  internalNotes?: string | null;
  internalAttachmentsUrl?: string | null;
  internalAttachmentsName?: string | null;
  submittedBy?: string | null;
  submittedByRole?: string | null;
  createdAt?: string | null;
  rejectedBy?: string | null;
  rejectedByRole?: string | null;
  rejectedAt?: string | null;
  resumedBy?: string | null;
  resumedByRole?: string | null;
  resumedAt?: string | null;
  lastTaskStatus?: string | null;
};

const InterruptionSideDrawer: React.FC<InterruptionSideDrawerProps> = ({
  isOpen,
  onClose,
  fakeOverrideStatus,
  externalNotesInterruption,
  externalAttachmentsUrlInterruption,
  externalAttachmentsNameInterruption,
  rejectionExternalNotes,
  rejectionExternalAttachmentsUrl,
  rejectionExternalAttachmentsName,
  rejectionInternalNotes,
  rejectionInternalAttachmentsUrl,
  rejectionInternalAttachmentsName,
  internalNotesInterruption,
  internalAttachmentsUrlInterruption,
  internalAttachmentsNameInterruption,
  externalNotes,
  externalAttachmentsUrl,
  externalAttachmentsName,
  internalNotes,
  internalAttachmentsUrl,
  internalAttachmentsName,
  status,
  submittedBy,
  submittedByRole,
  createdAt,
  rejectedBy,
  rejectedByRole,
  rejectedAt,
  resumedBy,
  resumedByRole,
  resumedAt,
  lastTaskStatus,
}) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadCertificate();
  const theme = useTheme();
  const dividerBorderTop = `1px solid ${theme.palette.strokeColor?.secondary ?? "#BBC1C5"}`;

  const { userAccessPermissions } = useAuthStore();
  const isMarineDepartment = checkPermission(userAccessPermissions, "hasChangeVesselStatusAccess");
  const effectiveStatus = fakeOverrideStatus || status;
  const isResumptionPending = effectiveStatus === "RESUME_PENDING";
  const showRejectionDetails = lastTaskStatus === "REJECTED" || lastTaskStatus === "RESUMED";
  const hasRejectionInfo = Boolean(
    rejectionExternalNotes ||
      rejectionExternalAttachmentsUrl ||
      rejectionInternalNotes ||
      rejectionInternalAttachmentsUrl,
  );
  const hasInterruptionContent = Boolean(
    externalNotesInterruption ||
      externalAttachmentsUrlInterruption ||
      internalNotesInterruption ||
      internalAttachmentsUrlInterruption,
  );
  const hasResumptionContent = Boolean(
    externalNotes || externalAttachmentsUrl || internalNotes || internalAttachmentsUrl,
  );
  const hasInterruptionMetadata = Boolean(submittedBy || submittedByRole || createdAt);
  const showRejectionSection = showRejectionDetails && hasRejectionInfo;
  const showInterruptionSection =
    !showRejectionSection &&
    (!isResumptionPending || hasInterruptionContent || hasInterruptionMetadata);
  const showResumptionSection = isResumptionPending && hasResumptionContent;
  const internalAttachmentName = internalAttachmentsName?.trim() || DEFAULT_ATTACHMENT_NAME;

  const externalAttachmentName = externalAttachmentsName?.trim() || DEFAULT_ATTACHMENT_NAME;

  const internalAttachmentInterruptionName =
    internalAttachmentsNameInterruption?.trim() || DEFAULT_ATTACHMENT_NAME;

  const externalAttachmentInterruptionName =
    externalAttachmentsNameInterruption?.trim() || DEFAULT_ATTACHMENT_NAME;

  const rejectionExternalAttachmentInterruptionName =
    rejectionExternalAttachmentsName?.trim() || DEFAULT_ATTACHMENT_NAME;

  const rejectionInternalAttachmentInterruptionName =
    rejectionInternalAttachmentsName?.trim() || DEFAULT_ATTACHMENT_NAME;

  const drawerTitle = isResumptionPending
    ? t("vessel-call.interruption.resume-pending-title")
    : t("vessel-call.interruption.title");

  const resolutionTitle = isResumptionPending
    ? t("vessel-call.resumption.details")
    : t("vessel-call.internal-notes");

  const renderDivider = (testId: string) => (
    <div data-testid={testId} className="my-4 w-full" style={{ borderTop: dividerBorderTop }} />
  );

  const renderSubmittedSection = (
    sectionSubmittedBy?: string | null,
    sectionSubmittedByRole?: string | null,
    sectionCreatedAt?: string | null,
  ) => {
    const submittedByName = translateNormalizedRole(t, sectionSubmittedBy);
    const submittedByRoleText = translateNormalizedRole(t, sectionSubmittedByRole);
    const hasSubmittedByInfo = Boolean(submittedByName || submittedByRoleText);
    const createdAtText = sectionCreatedAt ? formatDateTime(sectionCreatedAt) : "-";

    return (
      <div className="mt-4">
        <p className="text-xs text-gray-500">
          <span>{t("vessel-call.interruption.submittedBy")}:</span>{" "}
          {hasSubmittedByInfo ? (
            <>
              {submittedByName && <span className="text-gray-800">{submittedByName}</span>}
              {submittedByName && submittedByRoleText ? " / " : null}
              {submittedByRoleText}
            </>
          ) : (
            "-"
          )}
        </p>
        <p className="text-xs text-gray-500 mt-1">{createdAtText}</p>
      </div>
    );
  };

  return (
    <div data-testid="interruption-sidedrawer" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={drawerTitle}
        onClose={() => onClose("interruptionDrawer")}
        submitLabel={t("common.cancel")}
        onSubmit={() => onClose("interruptionDrawer")}
        hideCancelButton={true}
      >
        <div className="w-[400px] p-6">
          <div className="text-sm font-bold mb-1">{t("vessel-call.interruption.status")}</div>
          <p className="text-sm">{t(getStatusLabel(effectiveStatus ?? ""))}</p>

          {showInterruptionSection && externalNotesInterruption && (
            <div className="mt-4">
              <div className="text-sm font-bold mb-1">{t("vessel-call.interruption.reason")}</div>
              <p className="text-sm">{externalNotesInterruption}</p>
            </div>
          )}
          {showInterruptionSection &&
            externalAttachmentsUrlInterruption &&
            externalAttachmentInterruptionName && (
              <div className="mt-4">
                <div className="text-sm text-gray-500 font-semibold mb-1">
                  {t("vessel-call.external-attachments")}
                </div>
                <div className="border rounded p-3 w-full border-[#F1F2F4]">
                  <div className="flex justify-between items-center w-full">
                    <a
                      href={externalAttachmentsUrlInterruption}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline break-all cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePreview(
                          "document",
                          "bff-vessel-call",
                          externalAttachmentsUrlInterruption,
                        );
                      }}
                    >
                      {externalAttachmentInterruptionName}
                    </a>
                    <button
                      type="button"
                      onClick={() =>
                        handleDownload(
                          "document",
                          "bff-vessel-call",
                          externalAttachmentsUrlInterruption,
                          externalAttachmentInterruptionName,
                        )
                      }
                      className="cursor-pointer"
                      title={t("common.download")}
                    >
                      <FileDownloadOutlinedIcon />
                    </button>
                  </div>
                </div>
              </div>
            )}
          {showInterruptionSection && internalNotesInterruption && (
            <div className="mt-4">
              <div className="text-sm font-bold mb-1">{t("vessel-call.internal-notes")}</div>
              <p className="text-sm">{internalNotesInterruption}</p>
            </div>
          )}

          {isMarineDepartment &&
            showInterruptionSection &&
            internalAttachmentsUrlInterruption &&
            internalAttachmentInterruptionName && (
              <div className="mt-4">
                <div className="text-sm text-gray-500 font-semibold mb-1">
                  {t("vessel-call.internal-attachments")}
                </div>
                <div className="border rounded p-3 w-full border-[#F1F2F4]">
                  <div className="flex justify-between items-center w-full">
                    <a
                      href={internalAttachmentsUrlInterruption}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline break-all cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePreview(
                          "document",
                          "bff-vessel-call",
                          internalAttachmentsUrlInterruption,
                        );
                      }}
                    >
                      {internalAttachmentInterruptionName}
                    </a>
                    <button
                      type="button"
                      onClick={() =>
                        handleDownload(
                          "document",
                          "bff-vessel-call",
                          internalAttachmentsUrlInterruption,
                          internalAttachmentInterruptionName,
                        )
                      }
                      className="cursor-pointer"
                      title={t("common.download")}
                    >
                      <FileDownloadOutlinedIcon />
                    </button>
                  </div>
                </div>
              </div>
            )}
          {showInterruptionSection &&
            renderSubmittedSection(submittedBy, submittedByRole, createdAt)}
          {showInterruptionSection &&
            (showRejectionSection || showResumptionSection) &&
            renderDivider("interruption-section-divider")}

          {showRejectionSection && rejectionExternalNotes && (
            <div className="mt-4">
              <div className="text-sm font-bold mb-1">
                {t("vessel-call.interruption.reject-reason")}
              </div>
              <p className="text-sm">{rejectionExternalNotes}</p>
            </div>
          )}
          {showRejectionSection &&
          rejectionExternalAttachmentsUrl &&
          rejectionExternalAttachmentInterruptionName ? (
            <div className="mt-4">
              <div className="text-sm text-gray-500 font-semibold mb-1">
                {t("vessel-call.external-attachments")}
              </div>
              <div className="border rounded p-3 w-full border-[#F1F2F4]">
                <div className="flex justify-between items-center w-full">
                  <a
                    href={rejectionExternalAttachmentsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePreview("document", "bff-vessel-call", rejectionExternalAttachmentsUrl);
                    }}
                  >
                    {rejectionExternalAttachmentInterruptionName}
                  </a>
                  <button
                    type="button"
                    onClick={() =>
                      handleDownload(
                        "document",
                        "bff-vessel-call",
                        rejectionExternalAttachmentsUrl,
                        rejectionExternalAttachmentInterruptionName,
                      )
                    }
                    className="cursor-pointer"
                    title={t("common.download")}
                  >
                    <FileDownloadOutlinedIcon />
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {showRejectionSection && rejectionInternalNotes ? (
            <div className="mt-4">
              <div className="text-sm font-bold mb-1">{t("vessel-call.internal-notes")}</div>
              <p className="text-sm">{rejectionInternalNotes}</p>
            </div>
          ) : null}

          {isMarineDepartment ? (
            showRejectionSection &&
            rejectionInternalAttachmentsUrl &&
            rejectionInternalAttachmentInterruptionName ? (
              <div className="mt-4">
                <div className="text-sm text-gray-500 font-semibold mb-1">
                  {t("vessel-call.internal-attachments")}
                </div>
                <div className="border rounded p-3 w-full border-[#F1F2F4]">
                  <div className="flex justify-between items-center w-full">
                    <a
                      href={rejectionInternalAttachmentsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline break-all cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePreview(
                          "document",
                          "bff-vessel-call",
                          rejectionInternalAttachmentsUrl,
                        );
                      }}
                    >
                      {rejectionInternalAttachmentInterruptionName}
                    </a>
                    <button
                      type="button"
                      onClick={() =>
                        handleDownload(
                          "document",
                          "bff-vessel-call",
                          rejectionInternalAttachmentsUrl,
                          rejectionInternalAttachmentInterruptionName,
                        )
                      }
                      className="cursor-pointer"
                      title={t("common.download")}
                    >
                      <FileDownloadOutlinedIcon />
                    </button>
                  </div>
                </div>
              </div>
            ) : null
          ) : null}
          {showRejectionSection && renderSubmittedSection(rejectedBy, rejectedByRole, rejectedAt)}
          {showRejectionSection &&
            showResumptionSection &&
            renderDivider("rejection-section-divider")}

          {showResumptionSection && externalNotes && (
            <div className="mt-4">
              <div className="text-sm font-bold mb-1">{t("vessel-call.resumption.details")}</div>
              <p className="text-sm">{externalNotes}</p>
            </div>
          )}
          {showResumptionSection && externalAttachmentsUrl && externalAttachmentName && (
            <div className="mt-4">
              <div className="text-sm text-gray-500 font-semibold mb-1">
                {t("vessel-call.external-attachments")}
              </div>
              <div className="border rounded p-3 w-full border-[#F1F2F4]">
                <div className="flex justify-between items-center w-full">
                  <a
                    href={externalAttachmentsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePreview("document", "bff-vessel-call", externalAttachmentsUrl);
                    }}
                  >
                    {externalAttachmentName}
                  </a>
                  <button
                    type="button"
                    onClick={() =>
                      handleDownload(
                        "document",
                        "bff-vessel-call",
                        externalAttachmentsUrl,
                        externalAttachmentName,
                      )
                    }
                    className="cursor-pointer"
                    title={t("common.download")}
                  >
                    <FileDownloadOutlinedIcon />
                  </button>
                </div>
              </div>
            </div>
          )}
          {showResumptionSection && internalNotes && (
            <div className="mt-4">
              <div className="text-sm font-bold mb-1">{resolutionTitle}</div>
              <p className="text-sm">{internalNotes}</p>
            </div>
          )}
          {isMarineDepartment &&
            showResumptionSection &&
            internalAttachmentsUrl &&
            internalAttachmentName && (
              <div className="mt-4">
                <div className="text-sm text-gray-500 font-semibold mb-1">
                  {t("vessel-call.internal-attachments")}
                </div>
                <div className="border rounded p-3 w-full border-[#F1F2F4]">
                  <div className="flex justify-between items-center w-full">
                    <a
                      href={internalAttachmentsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline break-all cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePreview("document", "bff-vessel-call", internalAttachmentsUrl);
                      }}
                    >
                      {internalAttachmentName}
                    </a>
                    <button
                      type="button"
                      onClick={() =>
                        handleDownload(
                          "document",
                          "bff-vessel-call",
                          internalAttachmentsUrl,
                          internalAttachmentName,
                        )
                      }
                      className="cursor-pointer"
                      title={t("common.download")}
                    >
                      <FileDownloadOutlinedIcon />
                    </button>
                  </div>
                </div>
              </div>
            )}
          {showResumptionSection &&
            renderSubmittedSection(
              resumedBy ?? submittedBy,
              resumedByRole ?? submittedByRole,
              resumedAt ?? createdAt,
            )}
        </div>
      </SideDrawer>
    </div>
  );
};

export default InterruptionSideDrawer;
